import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useSlideRotation, inWindow } from "../lib/useSlideRotation";
import * as Icons from "lucide-react";
import { Search, MapPin, Sparkles, ArrowRight, PawPrint } from "lucide-react";
import { FreshMore } from "../components/FreshMore";
import { useHomeFeed } from "../lib/siteContent";
import { InfoBoxes } from "../components/home/InfoBoxes";
import { VerifyPromo, trackPromoClick } from "../components/home/VerifyPromo";
import { tileTheme } from "../lib/tileColor";
import { SeasonalBanner } from "../components/SeasonalBanner";
import { HolidayTint } from "../components/HolidayTint";
import { LogoAccent } from "../components/LogoAccent";
import { api, photoUrl, viewerId } from "../lib/api";
import { CATEGORIES, PROVINCES } from "../lib/constants";
import { CATEGORY_LABELS_FR } from "../lib/i18n";
import { useLanguage } from "../context/LanguageContext";
import { ListingCard } from "../components/ListingCard";
import { FeaturedCarousel } from "../components/FeaturedCarousel";
import { BannerAd } from "../components/BannerAd";
import { ReunionsStrip } from "../components/ReunionsStrip";
import { NearYou } from "../components/NearYou";
import { HeroBackdrop, POS_CLASS, shadowStyle } from "../components/HeroBackdrop";
import { CategoryCarousel } from "../components/CategoryCarousel";
import { fontStyle } from "../lib/googleFonts";
import { animProps, TypedTitle, heroBtnStyle } from "../lib/heroAnim";
import { homeTiles } from "./admin/CategoryOrder";
import { RotatingTagline } from "../components/RotatingTagline";
import { tilePhoto } from "../lib/categoryPhotos";
import { CategoryPhoto } from "../components/CategoryPhoto";
import { LostNearYou } from "../components/LostNearYou";
import { HomePopup } from "../components/HomePopup";
import { RecentlyViewed } from "../components/browse/RecentlyViewed";
import { UpcomingLittersHome } from "../components/litters/UpcomingLittersHome";
import { Button } from "../components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const HERO = "https://images.unsplash.com/photo-1450778869180-41d0601e046e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const HERO_DEFAULTS = {
  badge: "Canada's friendliest pet marketplace",
  title_line1: "Find your next",
  title_line2: "best friend.",
  subtitle: "Adopt, buy and sell pets, supplies and services from trusted, verified sellers across Canada.",
  stats_suffix: "active listings nationwide 🇨🇦",
};

export default function Home() {
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [nearIds, setNearIds] = useState([]);
  const [lostIds, setLostIds] = useState([]);
  const [spotIds, setSpotIds] = useState([]);
  const [freshCount, setFreshCount] = useState(16);
  const freshPool = useMemo(() => {
    const shown = new Set([...nearIds, ...lostIds, ...spotIds]);
    return listings.filter((l) => !shown.has(l.id));
  }, [listings, nearIds, lostIds, spotIds]);
  const freshListings = freshPool.slice(0, freshCount);
  const feed = useHomeFeed();
  const loadMore = useCallback(() => setFreshCount((n) => n + (feed.fresh_step || 4)), [feed.fresh_step]);
  const [meta, setMeta] = useState({ category_counts: {}, total: 0 });
  const [q, setQ] = useState("");
  const [prov, setProv] = useState("all");
  const [hero, setHero] = useState(null);
  const [catOverrides, setCatOverrides] = useState({});
  const [catSpeed, setCatSpeed] = useState(3);
  const [catOrder, setCatOrder] = useState([]);
  const [catHidden, setCatHidden] = useState([]);
  const [seasonBanner, setSeasonBanner] = useState(null);
  const [tint, setTint] = useState(null);
  const [accent, setAccent] = useState("");
  const [previewName, setPreviewName] = useState("");
  const previewId = new URLSearchParams(window.location.search).get("preview_schedule");
  const [marketing, setMarketing] = useState(null);
  const [bannerIdx, setBannerIdx] = useState(0);
  const { lang, t } = useLanguage();

  const [promoVariant] = useState(() => {
    try {
      let v = localStorage.getItem("verify_promo_variant");
      if (v !== "a" && v !== "b") { v = Math.random() < 0.5 ? "a" : "b"; localStorage.setItem("verify_promo_variant", v); }
      return v;
    } catch { return "a"; }
  });
  const [promoBox, setPromoBox] = useState(null);

  useEffect(() => {
    api.get("/site/verify-promo-box").then((r) => setPromoBox(r.data)).catch(() => {});
    api.get("/listings?sort=newest").then((r) => setListings(r.data));
    api.get("/meta").then((r) => setMeta(r.data));
    api.get("/site/hero").then((r) => setHero(r.data)).catch(() => setHero({}));
    api.get(previewId ? `/admin/site/categories/preview/${previewId}` : "/site/categories")
      .then((r) => { setCatOverrides(r.data.overrides || {}); setCatSpeed(r.data.carousel_speed || 3); setCatOrder(r.data.order || []); setCatHidden(r.data.hidden || []); setSeasonBanner(r.data.banner || null); setTint(r.data.tint || null); setAccent(r.data.accent || ""); if (r.data.preview) setPreviewName(r.data.active_schedule || "schedule"); })
      .catch(() => { if (previewId) setPreviewName("unavailable"); });
    api.get(`/site/marketing-banner?viewer=${encodeURIComponent(viewerId())}`).then((r) => setMarketing(r.data)).catch(() => {});
    api.post("/site/verify-promo/impression", { variant: promoVariant, viewer: viewerId() }).catch(() => {});
  }, [promoVariant, previewId]);

  const h = hero || HERO_DEFAULTS;
  const heroImg = photoUrl(hero && hero.hero_image) || HERO;
  const pick = (key) => (lang === "fr" ? (h[`${key}_fr`] || h[key]) : h[key]);
  const [strip, setStrip] = useState(null);
  const stripPicked = useRef(false);
  useEffect(() => {
    if (!hero || stripPicked.current) return;
    stripPicked.current = true;
    const list = (hero.hero_strips || []).filter((x) => x.enabled && x.desktop);
    if (!list.length) return;
    let last = null;
    try { last = localStorage.getItem("cp_last_hero_strip"); } catch { /* storage blocked */ }
    const pool = list.length > 1 ? list.filter((x) => x.id !== last) : list;
    const chosen = pool[Math.floor(Math.random() * pool.length)];
    try { localStorage.setItem("cp_last_hero_strip", chosen.id); } catch { /* storage blocked */ }
    setStrip(chosen);
  }, [hero]);
  const [typing, setTyping] = useState(false);
  const slides = (hero?.slides || []).filter((x) => x.enabled && inWindow(x));
  const [sIdx, setSIdx] = useSlideRotation(slides, typing);
  const fallback = (hero?.slides || []).find((x) => x.enabled && !x.start_at && !x.end_at) || { ...h, ...HERO_DEFAULTS, ...Object.fromEntries(["badge_fr", "title_line1_fr", "title_line2_fr", "subtitle_fr"].map((k) => [k, ""])) };
  const sl = slides[sIdx] || (hero ? { ...fallback, id: "default", buttons: [] } : h);
  const pickS = (key) => (lang === "fr" ? (sl[`${key}_fr`] || sl[key]) : sl[key]);
  useEffect(() => { (hero?.slides || []).forEach((x) => { const p = window.innerWidth < 640 && x.bg_image_mobile ? x.bg_image_mobile : x.bg_image; if (p) new Image().src = photoUrl(p); }); }, [hero]);

  const mBanners = marketing?.banners || [];
  // Weighted rotation: expand banners by weight into a sequence, then cycle through it.
  const seq = useMemo(() => {
    const s = [];
    mBanners.forEach((b, i) => { const w = Math.max(1, Math.min(10, b.weight || 1)); for (let k = 0; k < w; k++) s.push(i); });
    return s.length ? s : mBanners.map((_, i) => i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketing]);
  const bannerPausedRef = useRef(false);
  useEffect(() => {
    setBannerIdx(0);
    if (seq.length <= 1) return;
    const t = setInterval(() => { if (!bannerPausedRef.current) setBannerIdx((i) => (i + 1) % seq.length); }, 6000);
    return () => clearInterval(t);
  }, [seq.length]);

  const curBannerIdx = seq.length ? seq[bannerIdx % seq.length] : 0;
  const curBanner = mBanners[curBannerIdx];
  const trackBannerClick = (id) => { if (id) api.post("/site/marketing-banner/click", { banner_id: id }).catch(() => {}); };

  const doSearch = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (prov !== "all") p.set("province", prov);
    navigate(`/browse?${p.toString()}`);
  };

  return (
    <div>
      <HomePopup />
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#12234A]">
        <HeroBackdrop src={photoUrl(sl.bg_image) || heroImg} mobileSrc={sl.bg_image_mobile ? photoUrl(sl.bg_image_mobile) : ""} focus={sl.bg_focus} overlay={sl.overlay} zoom={sl.zoom} />
        <div className={`relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex ${hero?.hero_height ? "py-10" : "py-20 lg:py-28"}`}
          style={hero?.hero_height ? { minHeight: hero.hero_height } : undefined} data-testid="home-hero-inner">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
            <div className={`max-w-2xl ${POS_CLASS[hero?.hero_text_pos] || ""}`} data-testid="hero-text-col" data-pos={hero?.hero_text_pos || "center"}>
              <AnimatePresence mode="wait">
              <motion.div key={sl.id || "default"} {...animProps(sl.text_anim)} exit={{ opacity: 0, y: -8, transition: { duration: 0.18 } }} transition={{ duration: sl.text_anim === "zoom" ? 0.5 : 0.4 }} data-testid="hero-slide" data-slide-index={sIdx} data-anim={sl.text_anim || "slide-up"}>
              <span className="inline-flex items-center gap-2 bg-white/10 text-white text-xs font-semibold px-3 py-1.5 rounded-full backdrop-blur" style={{ ...fontStyle(sl.text_font), ...(sl.badge_color ? { color: sl.badge_color } : {}), ...(sl.badge_bg ? { backgroundColor: sl.badge_bg } : {}) }} data-testid="hero-badge">
                <Sparkles size={14} className={sl.badge_bg ? "" : "text-[#F5551E]"} /> {pickS("badge")}
              </span>
              <h1 className="mt-5 font-display text-4xl max-sm:[font-size:calc(2.25rem*var(--ms,1))] sm:[font-size:calc(3rem*var(--ds,1))] lg:[font-size:calc(3.75rem*var(--ds,1))] font-extrabold text-white leading-[1.05] tracking-tight flex items-start gap-3" style={{ ...shadowStyle(sl.text_shadow), "--ms": (sl.mobile_scale || 100) / 100, "--ds": (sl.desktop_scale || 100) / 100, ...fontStyle(sl.title_font) }} data-shadow={sl.text_shadow || "off"} data-font={sl.title_font || ""} data-testid="hero-h1">
                <img
                  src="https://customer-assets-7cd3h4nn.emergentagent.net/job_critter-classifieds/artifacts/r4i8kh4r_paw-logo.png"
                  alt="CritterPosts paw"
                  className="h-14 w-14 sm:h-16 sm:w-16 shrink-0 mt-1 animate-float object-contain"
                />
                <span data-testid="hero-title">
                  {sl.text_anim === "typewriter"
                    ? <TypedTitle line1={pickS("title_line1")} line2={pickS("title_line2")} run
                        line1Props={{ style: sl.line1_color ? { color: sl.line1_color } : undefined, "data-testid": "hero-title-line1" }}
                        line2Props={{ className: "text-[#F5551E]", style: sl.line2_color ? { color: sl.line2_color } : undefined, "data-testid": "hero-title-line2" }} />
                    : <><span style={sl.line1_color ? { color: sl.line1_color } : undefined} data-testid="hero-title-line1">{pickS("title_line1")}</span><br /><span className="text-[#F5551E]" style={sl.line2_color ? { color: sl.line2_color } : undefined} data-testid="hero-title-line2">{pickS("title_line2")}</span></>}
                </span>
              </h1>
              <p className="mt-5 text-lg max-sm:[font-size:calc(1.125rem*var(--ms,1))] sm:[font-size:calc(1.125rem*var(--ds,1))] text-slate-200 max-w-lg" style={{ ...shadowStyle(sl.text_shadow), "--ms": (sl.mobile_scale || 100) / 100, "--ds": (sl.desktop_scale || 100) / 100, ...fontStyle(sl.text_font), ...(sl.subtitle_color ? { color: sl.subtitle_color } : {}) }} data-font={sl.text_font || ""} data-testid="hero-subtitle">
                {pickS("subtitle")}
              </p>
              {(sl.buttons || []).length > 0 && (
                <div className="mt-6 flex flex-wrap gap-3" data-testid="hero-slide-buttons">
                  {sl.buttons.map((b, i) => {
                    const cls = `inline-flex items-center gap-2 h-12 px-6 rounded-xl font-semibold transition-[filter,background-color] hover:brightness-110 ${i === 0 ? (sl.btn_bg ? "" : "bg-[#F5551E] hover:bg-[#E04812]") + " text-white" : "border-2 border-white/40 text-white hover:bg-white/10"}`;
                    const st = heroBtnStyle(sl, i);
                    const label = lang === "fr" ? (b.label_fr || b.label) : b.label;
                    return b.link.startsWith("/")
                      ? <Link key={i} to={b.link} className={cls} style={st} data-testid={`hero-slide-btn-${i}`}>{label} {i === 0 && <ArrowRight size={17} />}</Link>
                      : <a key={i} href={b.link} target="_blank" rel="noopener noreferrer" className={cls} style={st} data-testid={`hero-slide-btn-${i}`}>{label} {i === 0 && <ArrowRight size={17} />}</a>;
                  })}
                </div>
              )}
              </motion.div>
              </AnimatePresence>
              {slides.length > 1 && (
                <div className="flex gap-1.5 mt-4" data-testid="hero-slide-dots">
                  {slides.map((x, i) => (
                    <button key={x.id || i} onClick={() => setSIdx(i)} aria-label={`Slide ${i + 1}`} data-testid={`hero-slide-dot-${i}`}
                      className={`h-1.5 rounded-full transition-all ${i === sIdx ? "w-6 bg-[#F5551E]" : "w-1.5 bg-white/30 hover:bg-white/50"}`} />
                  ))}
                </div>
              )}
              <RotatingTagline lines={hero?.taglines || [{ text: "Trusted pet listings from coast to coast 🇨🇦" }]} lang={lang} total={meta.total} speed={hero?.tagline_speed} className="mt-4 text-sm text-slate-300" />
            </div>

            <div className="w-full lg:max-w-md lg:ml-auto">
              <div className="relative">
                <picture>
                  {strip?.mobile && <source media="(max-width: 639px)" srcSet={photoUrl(strip.mobile)} />}
                  <img
                    src={strip ? photoUrl(strip.desktop) : "/catndogmacaw.png"}
                    data-strip-id={strip?.id || "default"}
                    alt=""
                    aria-hidden="true"
                    data-testid="hero-dog"
                    className={`pointer-events-none select-none block w-4/5 max-h-48 object-contain object-bottom mx-auto -mb-2 relative z-20 drop-shadow-sm transition-opacity duration-300 ${hero && (strip || !(hero.hero_strips || []).some((x) => x.enabled && x.desktop)) ? "opacity-100" : "opacity-0"}`}
                  />
                </picture>
                <div className="relative z-10 bg-white rounded-2xl p-2 flex flex-col gap-2 shadow-xl">
                  <div className="relative">
                    <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && doSearch()}
                      onFocus={() => setTyping(true)}
                      onBlur={() => setTyping(false)}
                      data-testid="hero-search-input"
                      placeholder={t("search_placeholder")}
                      className="w-full h-12 pl-11 pr-3 rounded-xl bg-slate-50 outline-none text-[#12234A]"
                    />
                  </div>
                  <Select value={prov} onValueChange={setProv}>
                    <SelectTrigger data-testid="hero-province-select" className="h-12 w-full rounded-xl bg-slate-50 border-0 text-[#12234A]">
                      <MapPin size={16} className="text-slate-400 mr-1" />
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {PROVINCES.map((p) => <SelectItem key={p.code} value={p.code}>{p.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button onClick={doSearch} data-testid="hero-search-button" className="h-12 px-8 rounded-xl bg-[#F5551E] hover:bg-[#E04812] font-semibold text-white">
                    {t("search_button")}
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-8 relative z-10">
        {previewName && (
          <div className="mb-3 inline-flex items-center gap-2 bg-amber-400 text-[#12234A] text-xs font-bold px-3 py-1.5 rounded-full shadow" data-testid="schedule-preview-badge">
            {previewName === "unavailable" ? "Preview unavailable — sign in as admin and save the schedule first" : `Preview mode · ${previewName} · only you can see this`}
          </div>
        )}
        {tint && <HolidayTint tint={tint} />}
        {accent && <LogoAccent accent={accent} />}
        {seasonBanner && <SeasonalBanner banner={seasonBanner} preview={!!previewName} />}
        <CategoryCarousel items={homeTiles(CATEGORIES.filter((c) => c.id !== "all"), catOrder, catHidden)} speed={catSpeed} renderItem={(c, dup) => {
          const ov = catOverrides[c.id] || {};
          const Icon = Icons[ov.icon] || Icons[c.icon] || Icons.PawPrint;
          const defaultLabel = lang === "fr" ? (CATEGORY_LABELS_FR[c.id] || c.label) : c.label;
          const label = ov.label || defaultLabel;
          const th = tileTheme(ov.color);
          return (
            <Link
              to={`/browse?category=${c.id}`}
              data-testid={dup ? undefined : `category-tile-${c.id}`}
              tabIndex={dup ? -1 : undefined}
              style={th?.style}
              className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border p-3 text-center card-hover h-full min-h-[104px] ${th ? "border-transparent shadow-md" : "bg-white border-slate-200/80"}`}
            >
              {tilePhoto(c.id, ov) ? (
                <CategoryPhoto src={tilePhoto(c.id, ov)} pos={ov.photo_pos} zoom={ov.photo_zoom} data-testid={dup ? undefined : `category-photo-${c.id}`}
                  className={`h-12 w-12 ring-2 ${th ? "ring-white/70" : "ring-[#FFE3D8]"} shadow-sm`} />
              ) : (
                <span className={`h-10 w-10 rounded-lg flex items-center justify-center ${th ? th.iconBg : "bg-[#FFF4F0]"}`}>
                  <Icon size={20} className={th ? th.icon : "text-[#F5551E]"} />
                </span>
              )}
              <span className={`text-xs sm:text-sm font-semibold leading-tight ${th ? th.label : "text-[#12234A]"}`}>{label}</span>
              {ov.description && <span className={`text-[10px] leading-tight ${th ? th.sub : "text-slate-400"}`}>{ov.description}</span>}
            </Link>
          );
        }} />
      </section>

      {/* Premium top-of-homepage banner — only renders when an advertiser has booked it */}
      <BannerAd variant="leaderboard_top" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" />


      {/* Featured spotlight carousel */}
      <FeaturedCarousel onItems={setSpotIds} />

      <NearYou onItems={setNearIds} exclude={spotIds} />

      <LostNearYou onItems={setLostIds} />

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-16">
        <div className="flex items-end justify-between mb-6">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-[#12234A]">{t("fresh_listings")}</h2>
            <p className="text-slate-500 mt-1">{t("fresh_sub")}</p>
          </div>
          <Link to="/browse" className="text-[#F5551E] font-semibold flex items-center gap-1 hover:gap-2 transition-all" data-testid="view-all-listings">
            {t("view_all")} <ArrowRight size={18} />
          </Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="fresh-listings-grid">
          {freshListings.map((l) => <ListingCard key={l.id} listing={l} />)}
        </div>
        <FreshMore mode={feed.fresh_mode} hasMore={freshPool.length > freshCount} count={freshCount} onMore={loadMore} t={t} />
      </section>

      <ReunionsStrip />

      <UpcomingLittersHome lang={lang} />

      {/* Ad */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
        <BannerAd variant="leaderboard" />
      </section>

      {feed.recent_row !== false && <RecentlyViewed t={t} lang={lang} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" testid="home-recently-viewed" desktop={6} />}

      <InfoBoxes lang={lang} />

      {/* Get Verified promo (admin-managed) */}
      {promoBox?.enabled && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-20" data-testid="home-get-verified-promo">
          <VerifyPromo s={promoBox} lang={lang} variant={promoVariant} onCtaClick={() => trackPromoClick(promoVariant)} />
        </section>
      )}

      {/* Admin-controlled marketing banner (not a paid ad) */}
      {marketing?.active && curBanner && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12" data-testid="home-marketing-banner"
          onMouseEnter={() => { bannerPausedRef.current = true; }}
          onMouseLeave={() => { bannerPausedRef.current = false; }}>
          {curBanner.link ? (
            <a href={curBanner.link} target="_blank" rel="noopener noreferrer" onClick={() => trackBannerClick(curBanner.id)} data-testid="home-marketing-link" className="block rounded-2xl overflow-hidden border border-slate-200/80 card-hover">
              <img src={photoUrl(curBanner.mobile_image || curBanner.image)} alt={curBanner.alt || "Featured"} data-testid="home-marketing-img-mobile" className="w-full aspect-[8/5] object-cover sm:hidden" />
              <img src={photoUrl(curBanner.image)} alt={curBanner.alt || "Featured"} data-testid="home-marketing-img-desktop" className="w-full aspect-[6/1] object-cover hidden sm:block" />
            </a>
          ) : (
            <div className="rounded-2xl overflow-hidden border border-slate-200/80">
              <img src={photoUrl(curBanner.mobile_image || curBanner.image)} alt={curBanner.alt || "Featured"} data-testid="home-marketing-img-mobile" className="w-full aspect-[8/5] object-cover sm:hidden" />
              <img src={photoUrl(curBanner.image)} alt={curBanner.alt || "Featured"} data-testid="home-marketing-img-desktop" className="w-full aspect-[6/1] object-cover hidden sm:block" />
            </div>
          )}
          {mBanners.length > 1 && (
            <div className="flex justify-center gap-1.5 mt-3" data-testid="marketing-banner-dots">
              {mBanners.map((b, i) => (
                <button key={b.id || i} onClick={() => setBannerIdx(Math.max(0, seq.indexOf(i)))} aria-label={`Banner ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === curBannerIdx ? "w-5 bg-[#F5551E]" : "w-1.5 bg-slate-300 hover:bg-slate-400"}`} />
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
